/**
 * Social Store - State management for Internet of Scent social features
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/services/supabase/client';
import type { ScentReport, ReporterProfile, Comment, FeedFilters, CreateReportData } from '@/types/social';

interface SocialState {
  // Feed
  feed: ScentReport[];
  feedLoading: boolean;
  feedError: string | null;
  hasMore: boolean;

  // Current profile being viewed
  viewingProfile: ReporterProfile | null;
  profileReports: ScentReport[];

  // Current report detail
  currentReport: ScentReport | null;
  currentComments: Comment[];

  // Leaderboard
  leaderboard: ReporterProfile[];

  // Actions
  fetchFeed: (filters: FeedFilters, page?: number) => Promise<void>;
  fetchProfile: (userId: string) => Promise<ReporterProfile | null>;
  fetchReport: (reportId: string) => Promise<void>;
  fetchComments: (reportId: string) => Promise<void>;
  fetchLeaderboard: () => Promise<void>;

  // Mutations
  createReport: (data: CreateReportData) => Promise<ScentReport>;
  likeReport: (reportId: string) => Promise<void>;
  unlikeReport: (reportId: string) => Promise<void>;
  addComment: (reportId: string, content: string) => Promise<void>;
  followUser: (userId: string) => Promise<void>;
  unfollowUser: (userId: string) => Promise<void>;
  deleteReport: (reportId: string) => Promise<void>;

  // Reset
  resetFeed: () => void;
}

const PAGE_SIZE = 20;

export const useSocialStore = create<SocialState>()(
  persist(
    (set, get) => ({
      feed: [],
      feedLoading: false,
      feedError: null,
      hasMore: true,
      viewingProfile: null,
      profileReports: [],
      currentReport: null,
      currentComments: [],
      leaderboard: [],

  fetchFeed: async (filters: FeedFilters, page = 0) => {
    set({ feedLoading: true, feedError: null });

    try {
      let query = supabase
        .from('scent_reports')
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name, reporter_id,
            avatar_url, is_verified_reporter, subscription_tier
          )
        `)
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      // Apply filters
      if (filters.type === 'user' && filters.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters.featured) {
        query = query.eq('is_featured', true);
      }
      if (filters.verified) {
        query = query.eq('is_verified_report', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      const reports = (data || []) as ScentReport[];

      // IMPORTANT: Preserve local reports (not yet saved to database)
      // when fetching from Supabase - don't overwrite them!
      if (page === 0) {
        const currentFeed = get().feed;
        // Keep all local reports (IDs starting with "local-")
        const localReports = currentFeed.filter((r) => r.id.startsWith('local-'));
        // Merge: local reports first, then Supabase reports
        const mergedFeed = [...localReports, ...reports];

        set({
          feed: mergedFeed,
          hasMore: reports.length === PAGE_SIZE,
          feedLoading: false,
        });
      } else {
        // For pagination, just append
        set({
          feed: [...get().feed, ...reports],
          hasMore: reports.length === PAGE_SIZE,
          feedLoading: false,
        });
      }
    } catch (error) {
      set({ feedError: (error as Error).message, feedLoading: false });
    }
  },

  fetchProfile: async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select(`
          *,
          badges:user_badges (
            badge:badges (*)
          )
        `)
        .eq('id', userId)
        .single();

      if (error) throw error;

      // Fetch user's reports
      const { data: reports } = await supabase
        .from('scent_reports')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      set({
        viewingProfile: profile as ReporterProfile,
        profileReports: (reports || []) as ScentReport[],
      });

      return profile as ReporterProfile;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  },

  fetchReport: async (reportId: string) => {
    try {
      const { data, error } = await supabase
        .from('scent_reports')
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name, reporter_id,
            avatar_url, is_verified_reporter, subscription_tier
          )
        `)
        .eq('id', reportId)
        .single();

      if (error) throw error;

      set({ currentReport: data as ScentReport });
    } catch (error) {
      console.error('Error fetching report:', error);
    }
  },

  fetchComments: async (reportId: string) => {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name, avatar_url, is_verified_reporter
          )
        `)
        .eq('report_id', reportId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Preserve local comments (not yet saved to database)
      const currentComments = get().currentComments;
      const localComments = currentComments.filter((c) =>
        typeof c.id === 'string' && c.id.startsWith('local-')
      );
      const fetchedComments = (data || []) as Comment[];

      // Merge local + fetched comments
      const mergedComments = [...localComments, ...fetchedComments];

      set({ currentComments: mergedComments });
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  },

  fetchLeaderboard: async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('reports_count', { ascending: false })
        .limit(50);

      if (error) throw error;

      set({ leaderboard: (data || []) as ReporterProfile[] });
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  },

  createReport: async (data: CreateReportData): Promise<ScentReport> => {
    // Generate a local report ID
    const localId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Extract reporter name from description
    const reporterName = data.description?.match(/Reporter: ([^\n]+)/)?.[1] || 'Anonymous';

    // Create local report object immediately for instant UI feedback
    const localReport: ScentReport = {
      id: localId,
      user_id: null,
      title: data.title || null,
      scent_notes: data.scent_notes || null,
      description: data.description || null,
      location_name: data.location_name || null,
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      aqi: data.aqi || null,
      pm25: data.pm25 || null,
      temperature: data.temperature || null,
      humidity: data.humidity || null,
      image_url: data.image_url || null,
      likes_count: 0,
      comments_count: 0,
      shares_count: 0,
      is_featured: false,
      is_verified_report: false,
      is_liked: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user: {
        id: 'anonymous',
        username: reporterName,
        first_name: reporterName,
        last_name: null,
        reporter_id: 'ANON',
        avatar_url: null,
        bio: null,
        reports_count: 0,
        followers_count: 0,
        following_count: 0,
        total_likes_received: 0,
        is_verified_reporter: false,
        is_admin: false,
        subscription_tier: 'free',
      },
    };

    // Add to feed immediately for instant UI feedback
    set({ feed: [localReport, ...get().feed] });

    // Save to database IMMEDIATELY (not fire-and-forget)
    // Critical for incognito mode - must complete before tab closes
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Save report with user_id if logged in, or null for anonymous
      const { data: report, error } = await supabase
        .from('scent_reports')
        .insert({ user_id: user?.id || null, ...data })
        .select()
        .single();

      if (error) {
        console.warn('⚠️ Failed to save scent report to database:', error.message);
        console.log('💾 Report saved locally only (will be lost in incognito mode)');
        // In incognito mode, this data will be lost when tab closes!
      } else if (report) {
        console.log('✅ Scent report saved to database:', report.id);
        // Replace local report with real one from database
        set({
          feed: get().feed.map((r) =>
            r.id === localId ? (report as ScentReport) : r
          ),
        });
        return report as ScentReport;
      }
    } catch (error) {
      console.error('Error saving report:', error);
    }

    return localReport;
  },

  likeReport: async (reportId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('likes')
        .insert({ user_id: user.id, report_id: reportId });

      // Update local state
      set({
        feed: get().feed.map((r) =>
          r.id === reportId
            ? { ...r, likes_count: r.likes_count + 1, is_liked: true }
            : r
        ),
      });
    } catch (error) {
      console.error('Error liking report:', error);
    }
  },

  unlikeReport: async (reportId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('likes')
        .delete()
        .eq('user_id', user.id)
        .eq('report_id', reportId);

      // Update local state
      set({
        feed: get().feed.map((r) =>
          r.id === reportId
            ? { ...r, likes_count: Math.max(0, r.likes_count - 1), is_liked: false }
            : r
        ),
      });
    } catch (error) {
      console.error('Error unliking report:', error);
    }
  },

  addComment: async (reportId: string, content: string) => {
    // Generate a local comment ID
    const localId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const { data: { user } } = await supabase.auth.getUser();

    // Create local comment immediately for instant UI feedback
    const localComment: Comment = {
      id: localId,
      user_id: user?.id || 'anonymous',
      report_id: reportId,
      content: content,
      created_at: new Date().toISOString(),
      user: user
        ? {
            id: user.id,
            username: user.email?.split('@')[0] || 'User',
            first_name: 'You',
            last_name: null,
            reporter_id: null,
            avatar_url: null,
            bio: null,
            reports_count: 0,
            followers_count: 0,
            following_count: 0,
            total_likes_received: 0,
            is_verified_reporter: false,
            is_admin: false,
            subscription_tier: 'free',
          }
        : {
            id: 'anonymous',
            username: 'Anonymous',
            first_name: 'Anonymous',
            last_name: null,
            reporter_id: null,
            avatar_url: null,
            bio: null,
            reports_count: 0,
            followers_count: 0,
            following_count: 0,
            total_likes_received: 0,
            is_verified_reporter: false,
            is_admin: false,
            subscription_tier: 'free',
          },
    };

    // Add to UI immediately
    set({
      currentComments: [...get().currentComments, localComment],
      feed: get().feed.map((r) =>
        r.id === reportId
          ? { ...r, comments_count: r.comments_count + 1 }
          : r
      ),
    });

    // Save to database IMMEDIATELY (critical for incognito mode)
    if (!user) {
      console.warn('⚠️ Not logged in - comment will be lost in incognito mode');
      return;
    }

    try {
      const { data: comment, error } = await supabase
        .from('comments')
        .insert({ user_id: user.id, report_id: reportId, content })
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name, avatar_url, is_verified_reporter
          )
        `)
        .single();

      if (error) {
        console.warn('⚠️ Failed to save comment to database:', error.message);
        console.log('💾 Comment will be lost in incognito mode');
      } else if (comment) {
        console.log('✅ Comment saved to database:', comment.id);
        // Replace local comment with real one
        set({
          currentComments: get().currentComments.map((c) =>
            c.id === localId ? (comment as Comment) : c
          ),
        });
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  },

  followUser: async (userId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('follows')
        .insert({ follower_id: user.id, following_id: userId });

      // Update viewing profile
      const profile = get().viewingProfile;
      if (profile && profile.id === userId) {
        set({
          viewingProfile: {
            ...profile,
            followers_count: profile.followers_count + 1,
            is_following: true,
          },
        });
      }
    } catch (error) {
      console.error('Error following user:', error);
    }
  },

  unfollowUser: async (userId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', userId);

      // Update viewing profile
      const profile = get().viewingProfile;
      if (profile && profile.id === userId) {
        set({
          viewingProfile: {
            ...profile,
            followers_count: Math.max(0, profile.followers_count - 1),
            is_following: false,
          },
        });
      }
    } catch (error) {
      console.error('Error unfollowing user:', error);
    }
  },

  deleteReport: async (reportId: string) => {
    try {
      await supabase
        .from('scent_reports')
        .delete()
        .eq('id', reportId);

      set({
        feed: get().feed.filter((r) => r.id !== reportId),
      });
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  },

  resetFeed: () => {
    set({ feed: [], hasMore: true, feedError: null });
  },
    }),
    {
      name: 'blurp-social-store',
      // Persist feed, comments, and leaderboard data across sessions
      partialize: (state) => ({
        feed: state.feed,
        currentComments: state.currentComments,
        leaderboard: state.leaderboard,
      }),
    }
  )
);
